<template>
  <div>
    <div class="document-info">
      <div class="mb-2">
        <div class="status-title" :class="{ displayNone: noHeader }">
          <i class="icon-bell info-color"></i>
          <h1 class="text-xl">
            {{ $t("documentDetail.documentStatusesInfo.documentStatuses") }}
          </h1>
        </div>
      </div>
      <div class="mb-2">
        <h2 class="text-base font-bold">
          {{ $t("documentDetail.documentStatusesInfo.documentGeneralInfo") }}
        </h2>
        <!--created-->
        <div class="flex flex-row items-center">
          <label-with-icon
            :margin-left="10"
            icon-name="calendar_month"
            :outlined="false"
            :label="docData.docStatuses.createdOn"
          />
        </div>
        <!--Signed-->
        <div v-if="docStatusData.signed" class="flex flex-row items-center">
          <label-with-icon
            :margin-left="10"
            icon-name="edit"
            :outlined="false"
            :label="$t('documentDetail.documentStatusesInfo.documentIsSigned')"
          />
        </div>

        <!--Visible-->
        <div
          v-if="docStatusData.docStatuses.showVisibility"
          class="flex flex-row items-center"
        >
          <div class="flex flex-row justify-center">
            <div v-html="iconsVisible" style="margin-left: 10px" />
            <div style="padding-left: 5px">
              <p v-if="docStatusData.docStatuses.visibility === 'visible'">
                {{
                  $t("documentDetail.documentStatusesInfo.documentIsVisible")
                }}
              </p>
              <p v-else>
                {{
                  $t("documentDetail.documentStatusesInfo.documentIsNotVisible")
                }}
              </p>
            </div>
          </div>
        </div>

        <!--Hold release-->
        <div
          v-if="showHoldRelease && textHoldRelease !== ''"
          class="flex flex-col"
        >
          <label-with-icon
            :margin-left="10"
            icon-name="edit_document"
            :outlined="false"
            :label="textHoldRelease"
          />
          <p v-if="docStatusData.holdReason" style="margin-left: 35px">
            {{ $t("documentDetail.documentStatusesInfo.holdReason") }}
            {{ docStatusData.holdReason }}
          </p>
        </div>

        <!--Viewed-->
        <div v-if="docData.viewedBy" class="flex flex-row items-center">
          <label-with-icon
            :margin-left="10"
            icon-name="person"
            :label="docData.viewedBy.name"
            :outlined="false"
          />
          <label-with-icon
            :margin-left="5"
            icon-name="calendar_month"
            :label="docData.viewedBy.time"
            :outlined="false"
          />
        </div>
      </div>

      <div v-if="docStatusData.removedChannels">
        <div
          v-for="(removedChannel, index) in docStatusData.removedChannels"
          :key="index"
        >
          <h2 class="text-base font-bold">
            {{ $t("documentDetail.documentStatusesInfo.removedChannel.title") }}
          </h2>
          <div class="flex flex-row items-center">
            <label-with-icon
              :margin-left="10"
              icon-name="mail"
              :label="removedChannel.removedBy"
              data-test="txtReason"
              icon-severity-color="info"
            />
            <label-with-icon
              :margin-left="10"
              icon-name="error_outline"
              :label="$t(translateType(removedChannel.channelType))"
            />
          </div>
        </div>
      </div>
      <div v-if="docStatusData.handledInfo && displayHandledError" class="mb-2">
        <h2 data-test="txtTitleErrorHandling" class="text-base font-bold">
          {{ $t("documentDetail.documentStatusesInfo.errorHandled") }}
        </h2>
        <div v-for="handledInfo in docStatusData.handledInfo">
          <div class="flex flex-row items-center">
            <label-with-icon
              :margin-left="10"
              icon-name="mail"
              :label="handledInfo.handledBy"
            />
            <label-with-icon
              :margin-left="5"
              icon-name="calendar_month"
              :label="handledInfo.handledAt"
              :outlined="false"
            />
          </div>
          <div
            v-if="handledInfo.reason"
            style="display: flex; flex-direction: row"
          >
            <label-with-icon
              :margin-left="10"
              icon-name="error_outline"
              :label="handledInfo.reason"
              data-test="txtReason"
              icon-severity-color="info"
              :center-icon="false"
            />
          </div>
        </div>
      </div>
      <div v-if="this.$store.state.mandateExists" class="mb-2">
        <h2 class="text-base font-bold">
          {{ $t("documentDetail.documentStatusesInfo.mandate") }}
        </h2>
        <div>
          <i class="info-color icon-notification padding-left" />
          {{ $t("documentDetail.documentStatusesInfo.mandateExists") }}
        </div>
      </div>

      <div
        v-if="
          docData.barCode.registeredMail &&
          docData.barCode.code &&
          displayTracking
        "
        class="mb-2"
      >
        <h2 class="text-base font-bold">
          {{
            $t("documentDetail.documentStatusesInfo.registeredMail.trackMail")
          }}
        </h2>
        <div>
          <div>
            <document-info-extended
              :prinInfo="docData.barCode.code"
              :printCSS="true"
            >
              <template #print>
                <i class="info-color icon-earth">
                  <a
                    target="_blank"
                    :href="registeredMailLink"
                    class="description"
                    >https://track.bpost.cloud...</a
                  >
                </i>
              </template>
            </document-info-extended>
          </div>
        </div>
      </div>

      <!-- AttachmentMails -->
      <document-statuses-mail
        type="attachMail"
        :mail-data="docStatusData.attachMail"
        :doc-id="docId"
        :legal-entity-id="legalEntityId"
        v-if="!docData.peppolFirstEnabled"
      />

      <!-- Email link -->
      <document-statuses-mail
        type="linkMail"
        :mail-data="docStatusData.linkEmail"
        :doc-id="docId"
        :legal-entity-id="legalEntityId"
        v-if="!docData.peppolFirstEnabled"
      />

      <!-- Edi -->
      <div
        class="mb-2"
        v-if="
          (this.$store.getters.getDocumentDetailData.held === undefined ||
            this.$store.getters.getDocumentDetailData.held === false) &&
          docStatusData.edi
        "
      >
        <h2 class="text-base font-bold">
          {{ $t("documentDetail.documentStatusesInfo.ediStatuses.title") }}
        </h2>
        <h2 class="edi-provider">
          {{ $t("documentDetail.documentStatusesInfo.ediProvider")
          }}{{ docData.ediProvider }}
        </h2>
        <div v-for="(item, index) in ediStatuses" :key="index">
          <div>
            <div
              :class="{ 'flex flex-row': !item.status.includes('tempFailure') }"
            >
              <label-with-icon
                :margin-left="20"
                icon-name="info"
                :label="
                  $t(item.status, { provider: docStatusData.ediProvider })
                "
                :icon-severity-color="item.error ? 'error' : 'info'"
              />
              <div class="flex flex-row align-items-center">
                <div v-if="item.message && item.status.includes('tempFailure')">
                  <p style="margin-left: 20px">{{ item.message }}</p>
                </div>
                <label-with-icon
                  v-if="item.time"
                  :margin-left="5"
                  icon-name="calendar_month"
                  :label="item.time"
                  :outlined="false"
                />
              </div>
            </div>
            <div v-if="item.message && !item.status.includes('tempFailure')">
              <p style="margin-left: 20px">
                {{
                  $t(translateEdiErrors(item.message)) !== ""
                    ? $t(translateEdiErrors(item.message))
                    : item.message
                }}

                <span
                  v-if="
                    item.message.match(regexEdiParticipant) !== null &&
                    !translateEdiErrors(item.message).includes('transportError')
                  "
                >
                  <span
                    v-for="(peppolId, index) in item.message.match(
                      regexEdiParticipant,
                    )"
                    :key="peppolId"
                  >
                    <span>{{ peppolId }}</span>
                    <span
                      v-if="
                        index <
                        item.message.match(regexEdiParticipant).length - 1
                      "
                    >
                      /
                    </span>
                  </span>
                </span>
              </p>
            </div>
            <p v-if="item.resendBy && showResendBy" class="ml-11">
              (<span class="italic">{{
                $t("documentDetail.documentStatusesInfo.resentBy")
              }}</span>
              {{ item.resendBy }})
            </p>
          </div>
        </div>
      </div>
      <div
        v-if="
          this.$store.getters.getDocumentDetailData.held === true &&
          docStatusData.edi
        "
      >
        <hold-information
          :title="$t('documentDetail.documentStatusesInfo.ediStatuses.title')"
          :description="
            $t('documentDetail.documentStatusesInfo.ediStatuses.onHold')
          "
          :information-receiver-text="
            $t(
              'documentDetail.documentStatusesInfo.ediStatuses.chosenPeppolChannel',
            ) +
            ' ' +
            docStatusData.ediProvider
          "
          :has-receivers-data="false"
        />
      </div>
      <!-- Print -->
      <div
        class="mb-2"
        v-if="
          !docData.peppolFirstEnabled &&
          (this.$store.getters.getDocumentDetailData.held === undefined ||
            this.$store.getters.getDocumentDetailData.held === false) &&
          docStatusData.print
        "
      >
        <h2 class="text-base font-bold">
          {{ $t("documentDetail.documentStatusesInfo.printStatuses.title") }}
        </h2>
        <div>
          <div v-for="(status, index) in docStatusData.print" :key="index">
            <status-item :item="status" itemType="print" />
            <p v-if="status.reprintedBy && showResendBy" class="ml-8">
              (<span class="italic">{{
                $t("documentDetail.documentStatusesInfo.resentBy")
              }}</span>
              {{ status.reprintedBy }})
            </p>
          </div>
        </div>
      </div>
      <div
        v-else-if="
          this.$store.getters.getDocumentDetailData.held === true &&
          docStatusData.print
        "
      >
        <hold-information
          :title="$t('documentDetail.documentStatusesInfo.printStatuses.title')"
          :description="
            $t('documentDetail.documentStatusesInfo.printStatuses.onHold')
          "
          :has-receivers-data="false"
        />
      </div>
      <!--Payment status-->
      <div v-if="docData.paymentUpdatesRaw && paymentStatus" class="mb-2">
        <h2 class="text-base font-bold">
          {{ $t("documentDetail.documentStatusesInfo.paymentInfo") }}
        </h2>
        <div v-for="(status, index) in paymentInfoFullStatus" :key="index">
          <div class="payment-status">
            <label-with-icon
              icon-name="info"
              :label="status.paymentStatusText"
              :icon-severity-color="status.error ? 'error' : 'info'"
              :margin-left="10"
              :font-size-icon="20"
              :center-icon="false"
            />
            <label-with-icon
              class="payment-status-date"
              v-if="status.paymentStatus !== 'OPEN'"
              icon-name="calendar_month"
              :label="status.date"
              icon-severity-color="info"
              :outlined="false"
            />
          </div>
          <p class="ml-6">{{ status.message }}</p>
        </div>
      </div>
      <!-- attachments -->
      <div v-if="attachmentFilesDisplay" class="mb-2">
        <div class="flex flex-row items-center">
          <h2 class="text-base font-bold with-icon">
            {{ $t("documentDetail.documentStatusesInfo.attachments.title") }}
          </h2>
          <div class="tooltip-outer">
            <i
              v-if="docStatusData.attachmentMerged"
              class="icon icon-notification info smaller-icon"
            >
            </i>
            <span
              class="tooltip-inner"
              style="font-size: 14px; font-weight: normal"
            >
              {{ $t("tooltips.attachmentsMerged") }}
            </span>
          </div>
        </div>

        <div
          v-for="attachFile in docData.attachmentFileNames"
          :key="attachFile.displayedName"
        >
          <div class="ml-3">
            <i class="icon icon-attachment" />
            <a
              class="attachment-file-link"
              :href="attachFile.href"
              target="_blank"
            >
              {{ attachFile.displayedName }}</a
            >
          </div>
        </div>
      </div>
      <!-- inserts -->
      <div v-if="insertsFilesDisplay" class="mb-2">
        <h2 class="text-base font-bold">
          {{ $t("documentDetail.documentStatusesInfo.inserts.title") }}
        </h2>
        <div
          v-for="insertsFile in docData.insertsFileNames"
          :key="insertsFile.displayedName"
        >
          <div class="ml-3">
            <i class="icon icon-attachment" />
            <a class="attachment-file-link" :href="insertsFile.href">
              {{ insertsFile.displayedName }}</a
            >
          </div>
        </div>
      </div>

      <!--choice Onboarding link-->
      <div v-if="onboarding" class="mb-2">
        <h2 class="text-base font-bold">
          {{ $t("documentDetail.documentStatusesInfo.onboardingLink") }}
        </h2>
        <div class="">
          <div class="channel-choice-outer">
            <div class="channel-choice">
              <div class="description" @click="copyOnboardingLink">
                <label-with-icon
                  :margin-left="7"
                  icon-name="public"
                  :label="onboardingShortLink"
                  :center-icon="false"
                />
              </div>
              <div class="tooltip tooltip-channel-choice">
                {{ $t("tooltips.clickToCopy") }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <!--Public link-->
      <div v-if="publicLink" class="mb-2">
        <h2 class="text-base font-bold">
          {{ $t("documentDetail.documentStatusesInfo.publicLink") }}
        </h2>
        <div>
          <div class="channel-choice-outer">
            <div class="channel-choice">
              <div class="description" @click="copyPublicLink">
                <label-with-icon
                  :margin-left="7"
                  icon-name="public"
                  :label="publicShortLink"
                />
              </div>
              <div class="tooltip tooltip-channel-choice">
                {{ $t("tooltips.clickToCopy") }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <!--Channel choice -->
      <div v-if="channelChoice" class="mb-2">
        <h2 class="text-base font-bold">
          {{ $t("documentDetail.documentStatusesInfo.channelChoiceLink") }}
        </h2>
        <div class="channel-choice-outer">
          <div class="channel-choice">
            <div class="description" @click="copyChannelChoiceLink">
              <label-with-icon
                :margin-left="7"
                icon-name="public"
                :label="channelChoiceShortLink"
                :center-icon="false"
              />
            </div>
            <div class="tooltip tooltip-channel-choice">
              {{ $t("tooltips.clickToCopy") }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DocumentInfoExtended from "../ui/DocumentInfoExtended.vue";
import i18n from "../../../i18n";
import userGroups from "../../../userGoups";
import StatusItem from "../../UI/StatusItem.vue";
import HoldInformation from "./HoldInformation.vue";
import LabelWithIcon from "../../UI/LabelWithIcon.vue";
import ResendDocument from "../resend/ResendDocument.vue";
import ConfirmationWithProps from "../../UI/ConfirmationWithProps.vue";
import DocumentStatusesMail from "@/components/documentDetail/statuses/DocumentStatusesMail.vue";
import moment from "moment/moment";

const EDI_ERROR_TRANSLATION_PATH =
  "documentDetail.documentStatusesInfo.ediErrors";
const EDI_ERROR_RULES = [
  {
    test: (msg) =>
      msg.includes(
        "Receiving access point could not confirm receipt of document",
      ),
    key: `${EDI_ERROR_TRANSLATION_PATH}.transportError`,
  },
  {
    test: (msg) => msg.includes("Wrapped Phase4Exception"),
    key: `${EDI_ERROR_TRANSLATION_PATH}.wrappedPhase`,
  },
  {
    test: (msg) => msg.includes("is a Hermes-only Participant"),
    key: `${EDI_ERROR_TRANSLATION_PATH}.hermesOnlyParticipant`,
  },
  {
    test: (msg) =>
      msg.includes("not reachable through Peppol Network") ||
      (msg.includes("Failed to send a document") &&
        msg.includes("via EDI service provider")),
    key: `${EDI_ERROR_TRANSLATION_PATH}.notReachableThroughPeppol`,
  },
  {
    test: (msg) =>
      msg.includes(
        'ERROR: duplicate key value violates unique constraint "nymus_peppol_retry_pkey"',
      ),
    key: `${EDI_ERROR_TRANSLATION_PATH}.dbDuplicateKeyError`,
  },
  {
    test: (msg) =>
      msg.includes("Failed to send a document") &&
      msg.includes(
        "A typical reason for `AskTimeoutException` is that the recipient actor didn't send a reply",
      ),
    key: `${EDI_ERROR_TRANSLATION_PATH}.peppolTimeout`,
  },
];

export default {
  name: "DocumentStatuses",
  mounted() {
    this.checkEmailErrors();
  },
  props: {
    docStatusData: {
      type: Object,
    },
    noHeader: {
      type: Boolean,
      default: false,
    },
    docId: String,
    legalEntityId: String,
  },
  components: {
    DocumentStatusesMail,
    ConfirmationWithProps,
    LabelWithIcon,
    HoldInformation,
    DocumentInfoExtended,
    StatusItem,
    ResendDocument,
  },
  data() {
    return {
      docData: this.docStatusData,
      channelChoiceLink: "",
      channelChoiceShortLink: "",
      onboardingLink: "",
      onboardingShortLink: "",
      publicShortLink: "",
      regexEdiParticipant: /[0-9]{4}:[a-zA-Z]{0,2}[0-9]*/g,
      smallScreen: window.innerWidth < 980,
    };
  },
  watch: {
    smallScreen() {
      this.smallScreen = window.innerWidth < 980;
    },
  },
  computed: {
    displayHandledError() {
      if (this.$route.name === "public") return false;
      return !userGroups.receiverNames.includes(
        this.$store.state.currentUserData.data.group,
      );
    },
    iconsVisible() {
      if (this.docStatusData.docStatuses.visibility === "visible") {
        return `<i class="icon icon-eye1 icon-color visibility-doc-icon" style="color: #215893"></i>`;
      } else
        return `<i class="icon icon-eye-blocked icon-color visibility-doc-icon" style="color: #215893"></i>`;
    },
    showHoldRelease() {
      return (
        !!this.$store.state.supplierGroup.data.holdRelease &&
        this.$store.getters.getDocumentDetailData.held !== undefined
      );
    },
    showResendBy() {
      return (
        userGroups.optipostNames.includes(
          this.$store.state.currentUserData.data.group,
        ) ||
        userGroups.supplierNames.includes(
          this.$store.state.currentUserData.data.group,
        )
      );
    },
    textHoldRelease() {
      if (
        this.$store.state.supplierGroup.data.holdRelease &&
        this.$store.getters.getDocumentDetailData.held !== undefined
      ) {
        if (
          this.$store.state.supplierGroup.data.holdRelease &&
          this.$store.getters.getDocumentDetailData.held
        ) {
          return i18n.t("documentDetail.documentStatusesInfo.documentWasHeld");
        }
      }
      return "";
    },
    paymentStatus() {
      if (
        this.$store.state.supplierGroup.data.channelsConfig.docImage
          ?.configPortal?.showPaymentStatus
      ) {
        return this.$store.state.supplierGroup.data.channelsConfig.docImage
          .configPortal.showPaymentStatus;
      }
      return false;
    },
    displayTracking() {
      const currentDate = new Date();
      let sixMonthsAgo = new Date();
      let invoiceDate = new Date(
        this.$store.state.documentDetailData.createdOn,
      );
      moment(sixMonthsAgo.setMonth(currentDate.getMonth() - 6)).format(
        "DD/MM/YYYY",
      );
      return invoiceDate >= sixMonthsAgo;
    },
    onboarding() {
      if (this.$route.name === "public") {
        return false;
      }

      if (
        this.docData.onboardingLink !== "" &&
        this.docData.onboardingLink !== undefined &&
        (userGroups.optipostNames.includes(
          this.$store.state.currentUserData.data.group,
        ) ||
          userGroups.supplierNames.includes(
            this.$store.state.currentUserData.data.group,
          ))
      ) {
        if (this.isDocValid()) {
          this.setOnboardingLink(this.docData.onboardingLink);
          this.setOnboardingShortLink(this.docData.onboardingLink);
          return true;
        }
        return false;
      }
      return false;
    },
    publicLink() {
      if (this.$route.name === "public") {
        return false;
      }

      if (
        this.docData.publicLink &&
        this.docData.accessCode &&
        (userGroups.optipostNames.includes(
          this.$store.state.currentUserData.data.group,
        ) ||
          userGroups.supplierNames.includes(
            this.$store.state.currentUserData.data.group,
          ))
      ) {
        this.setPublicShortLink(this.docData.publicLink);
        if (this.docData.docType === "mandateonboarding") {
          return this.isDocValid();
        }
        return true;
      }
      return false;
    },
    channelChoice() {
      if (this.$route.name === "public") {
        return false;
      }
      if (
        this.docData.channelChoiceLink !== "" &&
        this.docData.channelChoiceLink !== undefined &&
        (userGroups.optipostNames.includes(
          this.$store.state.currentUserData.data.group,
        ) ||
          userGroups.supplierNames.includes(
            this.$store.state.currentUserData.data.group,
          ))
      ) {
        this.setChannelChoiceLink(this.docData.channelChoiceLink);
        if (this.docData.channelChoiceLink) {
          this.setChannelChoiceShortLink(this.docData.channelChoiceLink);
        }
        return true;
      }
      return false;
    },

    registeredMailLink() {
      return (
        "https://track.bpost.cloud/btr/web/#/search?lang=" +
        this.$store.state.locale +
        "&itemCode=" +
        this.docData.barCode.code +
        "&postalCode=" +
        this.docStatusData.zipCode
      );
    },
    ediStatuses() {
      let ediStatusesArray = [];
      let statusNames = [];
      if (this.docData.edi === undefined) return [];
      this.docData.edi.forEach((item) => statusNames.push(item.status));

      for (let index in this.docData.edi) {
        ediStatusesArray.push(this.docData.edi[index]);
        if (
          this.docData.edi[index].status === "NOT_SENT" ||
          this.docData.edi[index].status === "SENT"
        ) {
          for (let j = ediStatusesArray.length - 1; j >= 0; j--) {
            if (ediStatusesArray[j].status === "TEMP_FAILURE") {
              ediStatusesArray.splice(j, 1);
            }
          }
        }
      }

      let ediStatuses = [];

      ediStatusesArray.forEach((status) => {
        let tempItem = {
          time: status.time,
          status: "",
          error: false,
        };

        if (status.message) tempItem.message = status.message;
        if (status.resendBy) tempItem.resendBy = status.resendBy;

        tempItem.status = `documentDetail.documentStatusesInfo.ediStatuses.${this.snakeToCamelCase(status.status)}`;

        ediStatuses.push(tempItem);
      });

      console.log(ediStatuses);

      return ediStatuses;
    },
    paymentInfoFullStatus() {
      let paymentStatuses = [];

      if (this.docData.paymentUpdatesRaw.length === 0) {
        let tempItem = {
          paymentStatus: "OPEN",
          date: moment(this.docData.docStatuses.createdOn).format("DD/MM/YYYY"),
          message: null,
        };
        paymentStatuses.push(tempItem);
      } else {
        this.docData.paymentUpdatesRaw.forEach((updateItem) => {
          let tempItem = {
            paymentStatus: updateItem.paymentStatus,
            date: moment(updateItem.date).format("DD/MM/YYYY"),
            message: updateItem.message ? updateItem.message : null,
          };
          paymentStatuses.push(tempItem);
        });
      }

      if (paymentStatuses.length > 1) {
        let deleteIndex = -1;
        paymentStatuses.forEach((status, index) => {
          if (status.paymentStatus === "OPEN") {
            deleteIndex = index;
          }
        });
        if (deleteIndex >= 0) paymentStatuses.splice(deleteIndex, 1);
      }
      let displayedStatuses = [];

      paymentStatuses.forEach((paymentStatus) => {
        let text = "";
        let error = false;
        if (paymentStatus.paymentStatus === "OPEN") {
          text = i18n.t(
            "documentDetail.documentStatusesInfo.paymentStatuses.open",
          );
        }
        if (paymentStatus.paymentStatus === "PAID") {
          text = i18n.t(
            "documentDetail.documentStatusesInfo.paymentStatuses.paid",
          );
        }
        if (paymentStatus.paymentStatus === "PAID_ONLINE") {
          text = i18n.t(
            "documentDetail.documentStatusesInfo.paymentStatuses.paidOnLine",
          );
        }
        if (paymentStatus.paymentStatus === "PAYMENT_CONFIRMED") {
          text =
            i18n.t(
              "documentDetail.documentStatusesInfo.paymentStatuses.confirmed",
            ) +
            " " +
            this.$store.state.supplierGroup.data.name;
        }
        if (paymentStatus.paymentStatus === "PENDING_TRAN") {
          text = i18n.t(
            "documentDetail.documentStatusesInfo.paymentStatuses.pendingTran",
          );
        }
        if (paymentStatus.paymentStatus === "PENDING") {
          text = i18n.t(
            "documentDetail.documentStatusesInfo.paymentStatuses.pending",
          );
        }
        if (paymentStatus.paymentStatus === "PAID_TRAN") {
          text = i18n.t(
            "documentDetail.documentStatusesInfo.paymentStatuses.paidTran",
          );
        }
        if (paymentStatus.paymentStatus === "ERROR_TEMP_TRAN") {
          text = i18n.t(
            "documentDetail.documentStatusesInfo.paymentStatuses.errorTempTran",
          );
          error = true;
        }
        if (paymentStatus.paymentStatus === "ERROR_TRAN") {
          text = i18n.t(
            "documentDetail.documentStatusesInfo.paymentStatuses.errorTran",
          );
          error = true;
        }

        displayedStatuses.push({
          paymentStatus: paymentStatus.paymentStatus,
          paymentStatusText: text,
          date: paymentStatus.date,
          error: error,
          message: paymentStatus.message ? paymentStatus.message : null,
        });
      });

      return displayedStatuses;
    },

    attachmentFilesDisplay() {
      return this.docData.attachmentFileNames.length;
    },
    insertsFilesDisplay() {
      return this.docData.insertsFileNames.length;
    },
  },
  methods: {
    snakeToCamelCase(str) {
      return str.toLowerCase().replace(/_([a-z])/g, (_, c) => c.toUpperCase());
    },
    isDocValid() {
      let endDate = new Date(this.docData.validityEnd);
      let today = new Date().getTime();
      endDate.setHours(23, 59, 59, 999);
      let validityEnd = new Date(endDate).getTime(); //milliseconds

      return validityEnd >= today;
    },
    displayNotification(name) {
      return name === "Hard bounce";
    },
    translateEdiErrors(message) {
      const rule = EDI_ERROR_RULES.find((r) => r.test(message));
      return rule?.key;
    },
    translateType(type) {
      const translation = {
        attachemail:
          "documentDetail.documentStatusesInfo.removedChannel.attachEmail",
        linkemail:
          "documentDetail.documentStatusesInfo.removedChannel.linkEmail",
        edi: "documentDetail.documentStatusesInfo.removedChannel.edi",
        print: "documentDetail.documentStatusesInfo.removedChannel.print",
      };
      return translation[type.toLowerCase()];
    },
    checkEmailErrors() {
      let emailErrors = {
        attachBounces: [],
        attachSpam: [],
        attachInactive: [],
        linkBounces: [],
        linkSpam: [],
        linkInactive: [],
      };

      if (this.docData.attachMail) {
        if (this.docData.attachMail.errorEmail) {
          this.docData.attachMail.errorEmail.forEach((item) => {
            emailErrors.attachBounces.push(item.email);
          });
        }
        if (this.docData.attachMail.spam) {
          this.docData.attachMail.spam.forEach((item) => {
            emailErrors.attachSpam.push(item.email);
          });
        }
        if (this.docData.attachMail.inactiveEmail) {
          this.docData.attachMail.inactiveEmail.forEach((item) => {
            emailErrors.attachInactive.push(item.email);
          });
        }
      }
      if (this.docData.linkEmail) {
        if (this.docData.linkEmail.errorEmail) {
          this.docData.linkEmail.errorEmail.forEach((item) => {
            emailErrors.linkBounces.push(item.email);
          });
        }
        if (this.docData.linkEmail.spam) {
          this.docData.linkEmail.spam.forEach((item) => {
            emailErrors.linkSpam.push(item.email);
          });
        }
        if (this.docData.linkEmail.inactiveEmail) {
          this.docData.linkEmail.inactiveEmail.forEach((item) => {
            emailErrors.linkInactive.push(item.email);
          });
        }
      }

      if (
        (userGroups.optipostNames.includes(
          this.$store.state.currentUserData.data.group,
        ) ||
          userGroups.supplierNames.includes(
            this.$store.state.currentUserData.data.group,
          )) &&
        this.$store.state.currentUserData.data.group !==
          "SupplierLimitedSubUser"
      ) {
        let keys = Object.keys(emailErrors);
        keys.forEach((key) => {
          emailErrors[key].forEach(async (email) => {
            await this.$store.dispatch("documents/checkEmailPostMark", {
              email: email,
              errorType: key,
            });
          });
        });
      }
    },
    setChannelChoiceLink(link) {
      this.channelChoiceLink = link;
    },
    setChannelChoiceShortLink(link) {
      let shortLink = link;
      if (link) {
        shortLink =
          "https://" + shortLink.slice(0, this.smallScreen ? 40 : 50) + "...";
        this.channelChoiceShortLink = shortLink;
      }
    },
    setOnboardingLink(link) {
      this.onboardingLink = link;
    },
    setOnboardingShortLink(link) {
      let shortLink = link;
      if (link) {
        shortLink =
          "https://" + shortLink.slice(0, this.smallScreen ? 40 : 50) + "...";
        this.onboardingShortLink = shortLink;
      }
    },
    setPublicShortLink(link) {
      let shortLink = link;
      if (link) {
        shortLink = shortLink.slice(0, this.smallScreen ? 40 : 50) + "...";
        this.publicShortLink = shortLink;
      }
    },
    copyOnboardingLink() {
      navigator.clipboard.writeText("https://" + this.onboardingLink);
    },
    copyPublicLink() {
      navigator.clipboard.writeText(this.docData.publicLink);
    },
    copyChannelChoiceLink() {
      navigator.clipboard.writeText("https://" + this.channelChoiceLink);
    },
    printStatus(printStatus) {
      switch (printStatus) {
        case "QUEUED":
          return i18n.t(
            "documentDetail.documentStatusesInfo.printStatuses.queued",
          );
        case "SENT_TO_PRINTER":
          return i18n.t(
            "documentDetail.documentStatusesInfo.printStatuses.sentToPrinter",
          );
        case "PRINTER_PROCESSING":
          return i18n.t(
            "documentDetail.documentStatusesInfo.printStatuses.printerProcessing",
          );
        case "PROCESSED_BY_PRINTER":
          return i18n.t(
            "documentDetail.documentStatusesInfo.printStatuses.processedByPrinter",
          );
        case "RE_PROCESSED_BY_PRINTER":
          return i18n.t(
            "documentDetail.documentStatusesInfo.printStatuses.reProcessedByPrinter",
          );
        case "RE_SENT_TO_PRINTER":
          return i18n.t(
            "documentDetail.documentStatusesInfo.printStatuses.reSentToPrinter",
          );
        case "RE_QUEUED":
          return i18n.t(
            "documentDetail.documentStatusesInfo.printStatuses.reQueued",
          );
        case "PRINTER_RETURNED":
          return i18n.t(
            "documentDetail.documentStatusesInfo.printStatuses.printerReturned",
          );
      }
      return printStatus;
    },
  },
};
</script>

<style scoped>
.document-info {
  padding-left: var(--generalPaddings);
}

.status-title {
  display: grid;
  grid-template-columns: 25px auto;
  grid-template-rows: 100%;
  grid-auto-flow: row;
  border-bottom: 1px solid lightgrey;
  padding-bottom: 10px;
  margin-top: 10px;
}

.status-title i {
  font-size: 20px;
  display: grid;
  grid-template-rows: 100%;
  grid-template-columns: 100%;
  grid-auto-flow: row;
  align-items: center;
}

.info-color {
  color: var(--infoColor);
}

.icon-attachment {
  color: var(--infoColor);
  margin-right: 5px;
}

.attachment-file-link {
  color: var(--infoColor);
  cursor: pointer;
}

.payment-status {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.payment-status-date {
  margin-left: 5px !important;
}

.attachment-file-link:hover {
  text-decoration: underline;
}

.displayNone {
  display: none;
}

.description {
  color: var(--theme-primary-500);
  font-size: 14px;
  padding-left: 5px;
  margin-right: 20px;
}

i,
.description {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  font-size: 14px;
}

@media only screen and (max-width: 1100px) and (min-width: 1000px) {
  .status-title h1 {
    font-size: 1.3rem;
  }
}

@media screen and (max-width: 1250px) and (min-width: 1000px) {
  .payment-status {
    flex-direction: column;
    align-items: start;
  }

  .payment-status-date {
    margin-left: 25px !important;
  }
}

@media only screen and (max-width: 650px) {
  .payment-status {
    flex-direction: column;
    align-items: start;
  }

  .payment-status-date {
    margin-left: 25px !important;
  }
}

.tooltip {
  padding: 0 4px;
  justify-self: start;
}

.with-icon {
  display: flex;
  align-items: center;
}

.smaller-icon {
  font-size: 16px;
  margin-left: 10px;
}

.channel-choice:hover .tooltip-channel-choice {
  display: grid;
}

.channel-choice {
  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: 100%;
  justify-self: start;
  cursor: pointer;
}

.channel-choice .description,
.channel-choice .tooltip {
  grid-column: 1/2;
  grid-row: 1/2;
}

.channel-choice .description {
  justify-self: start;
}

.channel-choice .tooltip {
  justify-self: center;
}

.channel-choice-outer {
  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: 100%;
}

i {
  margin-right: 4px;
}

.edi-provider {
  font-size: 16px;
  margin-left: 10px;
}

.padding-left {
  margin-left: var(--generalPaddings);
}

@media (max-width: 550px) {
  .document-info {
    padding-left: 0;
  }

  .edi-provider,
  .padding-left {
    margin-left: calc(0.5 * var(--generalPaddings));
  }
}
</style>
